import { useEffect, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, Link } from 'react-router-dom';
import { Button, Modal, OverlayTrigger, Tooltip } from 'react-bootstrap';
import CustomModal from '../../hoc/customModal';
import AddOperationalLog from './add';
import EditOperationalLog from './edit';
import { Loader } from '../../utils/tools';
import { fetchOperationalLogs, deleteOperationalLog } from '../../store/actions/operationalLog';
import { ReactComponent as DocumentIcon } from '../../assets/document.svg';
import { ReactComponent as EditIcon } from '../../assets/edit.svg';
import { ReactComponent as RemoveIcon } from '../../assets/remove.svg';
import { FACILITY_TYPE_LABELS } from '../../utils/constants/facilityTypes';
import { PLAN_LIMITS } from '../../config/plans';

const CONSENT_STATUS_CONFIG = {
    expiring: { label: 'Súhlas vyprší čoskoro', color: '#C97D2C', bg: '#FBEBD8' },
    expired: { label: 'Súhlas vypršal', color: '#A5432B', bg: '#F7E4DE' },
};

const UpgradeGate = () => (
    <div className="text-center py-5">
        <div style={{
            width: 64, height: 64, borderRadius: '50%', backgroundColor: '#F4F5FE',
            display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 24,
            margin: '0 auto 24px',
        }}>
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#0B1157" strokeWidth="1.8">
                <rect x="3" y="11" width="18" height="11" rx="2" />
                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
            </svg>
        </div>
        <h5 style={{ color: '#0B1157', marginBottom: '8px' }}>Prevádzkový denník</h5>
        <p className="text-muted mb-4" style={{ maxWidth: '420px', margin: '0 auto 24px' }}>
            Táto funkcia je dostupná len v pláne <strong>Business</strong>. Váš aktuálny plán (<strong>Start</strong>) prístup k prevádzkovému denníku nezahŕňa.
        </p>
        <Link to="/billing" className="btn button-custom-large radius">
            Prejsť na Business plán
        </Link>
    </div>
);

const DemoBanner = ({ logCount, maxLogs, maxEntries }) => (
    <div className="alert alert-warning d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
        <span>
            Demo plán: maximálne <strong>{maxLogs}</strong> {maxLogs === 1 ? 'denník' : 'denníky'} a <strong>{maxEntries}</strong> denných záznamov na denník.
            {logCount >= maxLogs ? ' Dosiahli ste limit denníkov.' : ''}
        </span>
        <Link to="/billing" className="btn btn-sm btn-warning fw-semibold" style={{ whiteSpace: 'nowrap' }}>
            Prejsť na Business plán
        </Link>
    </div>
);

const OperationalLogs = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const tenant = useSelector(state => state.tenant.current);
    const currentBranch = useSelector(state => state.branch.current);
    const logs = useSelector(state => state.operationalLog.data);
    const loading = useSelector(state => state.operationalLog.loading);
    const addedLog = useSelector(state => state.operationalLog.addedLog);
    const editedLog = useSelector(state => state.operationalLog.editedLog);

    const [isAddModalVisible, setIsAddModalVisible] = useState(false);
    const [isEditModalVisible, setIsEditModalVisible] = useState(false);
    const [logToEdit, setLogToEdit] = useState(null);
    const [show, setShow] = useState(false);
    const [logIdToDelete, setLogIdToDelete] = useState(null);

    const planLimits = PLAN_LIMITS[tenant?.plan];
    const hasAccess = planLimits?.maxOperationalLogs > 0;
    const isDemo = tenant?.plan === 'demo';
    const list = useMemo(() => (Array.isArray(logs) ? logs : []), [logs]);
    const isAtLogLimit = isDemo && list.length >= (planLimits?.maxOperationalLogs ?? 0);

    useEffect(() => {
        if (currentBranch && hasAccess) dispatch(fetchOperationalLogs({ branchId: currentBranch }));
    }, [dispatch, addedLog, editedLog, currentBranch, hasAccess]);

    const handleOpenAddModal = () => setIsAddModalVisible(true);
    const handleCloseAddModal = () => setIsAddModalVisible(false);

    const handleEditButtonClick = (logId) => {
        setLogToEdit(null);
        setLogToEdit(logId);
        setIsEditModalVisible(true);
    };

    const handleCloseEditModal = () => {
        setIsEditModalVisible(false);
        setLogToEdit(null);
    };

    const handleClose = () => setShow(false);
    const handleShow = () => setShow(true);

    const handleRemoveButtonClick = (logId) => {
        setLogIdToDelete(logId);
        handleShow();
    };

    const handleDeleteLog = () => {
        dispatch(deleteOperationalLog(logIdToDelete)).then(() => {
            if (currentBranch) dispatch(fetchOperationalLogs({ branchId: currentBranch }));
        });
        handleClose();
    };

    if (!hasAccess) {
        return <UpgradeGate />;
    }

    return (
        <>
            {isDemo ? (
                <DemoBanner
                    logCount={list.length}
                    maxLogs={planLimits.maxOperationalLogs}
                    maxEntries={planLimits.maxOperationalLogEntries}
                />
            ) : null}

            <div className="d-flex justify-content-end align-items-center mb-3 flex-wrap gap-2">
                <Button
                    className="button-custom-large radius"
                    onClick={handleOpenAddModal}
                    disabled={isAtLogLimit}
                >
                    Pridať denník
                </Button>
            </div>

            {loading ? (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '35vh' }}>
                    <Loader />
                </div>
            ) : list.length === 0 ? (
                <div className="text-center text-muted py-5">
                    <p className="mb-1"><strong>Zatiaľ nemáte žiadny prevádzkový denník.</strong></p>
                    <p className="mb-0">Kliknutím na tlačidlo <em>Pridať denník</em> vytvoríte prvý denník.</p>
                </div>
            ) : (
                list.map((log) => (
                    <div
                        key={log._id}
                        style={{
                            display: 'flex', alignItems: 'center', gap: '16px',
                            backgroundColor: '#fff', border: '1px solid #EDF2F7',
                            borderRadius: '10px', padding: '14px 18px', marginBottom: '10px'
                        }}
                    >
                        <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: '14px', fontWeight: 600, color: '#0B1157' }}>
                                {log.name}
                            </div>
                            <div style={{ fontSize: '12px', color: '#718094', marginTop: '2px' }}>
                                {FACILITY_TYPE_LABELS[log.facilityType] ?? log.facilityType} · Kód nakladania: {log.loadingCode ?? '-'}
                            </div>
                            <div style={{ fontSize: '11.5px', color: '#A9B0AE', marginTop: '2px' }}>
                                Súhlas: {log.consent?.name} ({log.consent?.decisionNumber})
                            </div>
                            {CONSENT_STATUS_CONFIG[log.consent?.status] ? (
                                <div
                                    style={{
                                        display: 'inline-block', fontSize: '11px', fontWeight: 600, marginTop: '6px',
                                        padding: '2px 8px', borderRadius: '999px',
                                        color: CONSENT_STATUS_CONFIG[log.consent.status].color,
                                        backgroundColor: CONSENT_STATUS_CONFIG[log.consent.status].bg
                                    }}
                                >
                                    {CONSENT_STATUS_CONFIG[log.consent.status].label}
                                </div>
                            ) : null}
                        </div>

                        <div className="d-flex gap-1" style={{ flexShrink: 0 }}>
                            <OverlayTrigger placement="top" overlay={<Tooltip>Vstúpiť do denníka</Tooltip>}>
                                <Button className="btnIcon" onClick={() => navigate(`/operational-log/${log._id}`)}>
                                    <DocumentIcon className="icon" alt="Vstúpiť do denníka" />
                                </Button>
                            </OverlayTrigger>

                            <OverlayTrigger placement="top" overlay={<Tooltip>Upraviť denník</Tooltip>}>
                                <Button className="btnIcon" onClick={() => handleEditButtonClick(log._id)}>
                                    <EditIcon className="icon icon-edit" alt="Upraviť" />
                                </Button>
                            </OverlayTrigger>

                            <OverlayTrigger placement="top" overlay={<Tooltip>Vymazať denník</Tooltip>}>
                                <Button className="btnIcon" onClick={() => handleRemoveButtonClick(log._id)}>
                                    <RemoveIcon className="icon icon-remove" alt="Vymazať" />
                                </Button>
                            </OverlayTrigger>
                        </div>
                    </div>
                ))
            )}

            <Modal show={show} onHide={handleClose}>
                <Modal.Header closeButton>
                    <Modal.Title>Potvrdenie</Modal.Title>
                </Modal.Header>

                <Modal.Body>
                    Naozaj chcete vymazať tento prevádzkový denník? Vymažú sa aj všetky jeho denné záznamy.
                </Modal.Body>

                <Modal.Footer>
                    <Button variant="secondary" onClick={handleClose}>
                        Zrušiť
                    </Button>
                    <Button variant="danger" onClick={handleDeleteLog}>
                        Vymazať
                    </Button>
                </Modal.Footer>
            </Modal>

            <CustomModal isVisible={isAddModalVisible} onClose={handleCloseAddModal}>
                <AddOperationalLog onClose={handleCloseAddModal} />
            </CustomModal>

            <CustomModal isVisible={isEditModalVisible} onClose={handleCloseEditModal}>
                {logToEdit && isEditModalVisible ? (
                    <EditOperationalLog operationalLogId={logToEdit} onClose={handleCloseEditModal} />
                ) : null}
            </CustomModal>
        </>
    );
};

export default OperationalLogs;
